import { flatten, values } from 'ramda';

import {
  lockRoute,
  setRequests,
  setStops,
  setTransferFromRouteId,
  setTransferredStops,
  setTransferRouteId,
} from '../../../reducers/route';

import { showNotification } from '../../notifications';
import { NotificationType } from '../../../../features/Notifier';
import { DISMISS } from '../../../../constants/strings';
import {
  getSelectedParcels,
  getSelectedStops,
} from '../../../selectors/ui/routeDetail';
import {
  findStopByStopNumber,
  isCollectionStop,
} from '../../../../models/stop';
import {
  generateCollectionStopTransferRequests,
  getValidateParcelList,
  processParcelTransferToRequests,
} from '../../../../models/request';
import {
  getDeliveryRouteStops,
  getOriginalParcelsTransferData,
  getRequestOrder,
  getRouteDetail,
  getRouteRequests,
  getRouteStops,
  getRouteTransferredStops,
} from '../../../selectors/route';
import {
  removeSelectedStops,
  updateSeqAndDisplayNumbers,
} from '../../../../models/moveStops';
import { isRouteStatusLoading } from '../../../../models/routeDetail';
import { updateStopInStopList } from '../../../../models/route';
import { validateStopMerge } from '../../stop';
import { getErrorMessage } from '../../../../utils/errors';
import { getAffectedStops } from '../../../../models/editStop';
import { clearSelection } from '../../../reducers/ui/routeDetail/stopFilters';

export const processStopTransfer =
  (transferRouteId, currentRoute, processedData = {}) =>
  async (dispatch, getState) => {
    const state = getState();
    const { stop, routeStops, transferredRouteStops } = processedData;
    const selectedStops = stop ? [stop] : getSelectedStops(state);
    const originalRouteParcels = getOriginalParcelsTransferData(state);

    const stops = routeStops || getRouteStops(state);
    const transferredStops =
      transferredRouteStops || getRouteTransferredStops(state);
    const requestOrder = getRequestOrder(state);
    let requests = [];
    selectedStops.forEach((stop) => {
      if (isCollectionStop(stop)) {
        requests.push(
          generateCollectionStopTransferRequests(
            currentRoute.routeId,
            transferRouteId,
            stop,
            requestOrder
          )
        );
      } else {
        requests.push(
          processParcelTransferToRequests(
            transferRouteId,
            stop,
            originalRouteParcels,
            stop.parcel,
            requestOrder
          )
        );
      }
    });

    const updatedStops = removeSelectedStops(stops, selectedStops);

    const updatedTransferredStops = [...transferredStops, ...selectedStops];

    return {
      updatedStops,
      requests: flatten(requests),
      transferredStop: updatedTransferredStops,
    };
  };

export const processParcelTransfer =
  (transferRouteId, currentRoute, processedData = {}) =>
  async (dispatch, getState) => {
    const state = getState();
    const originalRouteParcels = getOriginalParcelsTransferData(state);
    const { stop, parcels, routeStops, transferredRouteStops } = processedData;
    const stops = routeStops || getRouteStops(state);
    const selectedStop = stop ? [stop] : getSelectedStops(state);
    const selectedParcels = parcels || values(getSelectedParcels(state));
    const transferredStops =
      transferredRouteStops || getRouteTransferredStops(state);
    const requestOrder = getRequestOrder(state);
    const requests = processParcelTransferToRequests(
      transferRouteId,
      selectedStop[0],
      originalRouteParcels,
      selectedParcels,
      requestOrder
    );

    const toValidateParcelList = getValidateParcelList(
      selectedStop[0],
      selectedParcels
    );

    if (toValidateParcelList.length > 0) {
      const updatedStop = await dispatch(
        validateStopParcels(toValidateParcelList)
      );

      return updatedStop
        ? {
            requests,
            updatedStops: updateStopInStopList(stops, {
              ...selectedStop[0],
              ...updatedStop,
            }),
            transferredStop: transferredStops,
          }
        : {};
    }

    return {
      requests,
      updatedStops: removeSelectedStops(stops, selectedStop),
      transferredStop: [...transferredStops, ...selectedStop],
    };
  };

export const updateTransferEntities =
  ({ updatedStops, requests, transferredStop, transferRouteId }) =>
  (dispatch, getState) => {
    const state = getState();
    const currentRoute = getRouteDetail(state);
    const routeRequests = getRouteRequests(state);

    if (requests) {
      const updatedStopList = updateSeqAndDisplayNumbers(
        updatedStops,
        !isRouteStatusLoading(currentRoute)
      );
      dispatch(clearSelection());
      dispatch(setStops(updatedStopList));
      transferredStop && dispatch(setTransferredStops(transferredStop));
      dispatch(setRequests(routeRequests.concat(requests)));
      dispatch(setTransferRouteId(transferRouteId));
      dispatch(setTransferFromRouteId(currentRoute.routeId));
      return requests;
    }
    return false;
  };

export const processTransfer =
  (isStopsSelected, transferRouteId, processedData = {}) =>
  async (dispatch, getState) => {
    const currentUserLock = await dispatch(lockRoute());

    if (!currentUserLock) {
      return false;
    }
    const state = getState();
    const currentRoute = getRouteDetail(state);

    const processFunction = isStopsSelected
      ? processStopTransfer
      : processParcelTransfer;
    const { updatedStops, requests, transferredStop } = await dispatch(
      processFunction(transferRouteId, currentRoute, processedData)
    );
    return await dispatch(
      updateTransferEntities({
        updatedStops,
        requests,
        transferredStop,
        transferRouteId,
      })
    );
  };

export const onTransferMixed =
  (transferRouteId, selectedStops) => async (dispatch, getState) => {
    const currentUserLock = await dispatch(lockRoute());

    if (!currentUserLock) {
      return false;
    }
    const state = getState();
    const stops = getRouteStops(state);
    const currentRoute = getRouteDetail(state);

    const dataToUpdate = await selectedStops.reduce(
      async (processedData, stop, index) => {
        let data = await processedData;
        const originalStop = findStopByStopNumber(stops, stop.stopNumber);
        const { parcel = [] } = stop;
        const processFunction =
          originalStop.parcel.length === parcel.length
            ? processStopTransfer
            : processParcelTransfer;
        const additionalData =
          index === 0
            ? { stop: originalStop, parcels: parcel }
            : {
                stop: originalStop,
                parcels: parcel,
                routeStops: data.updatedStops,
                transferredRouteStops: data.transferredStop,
              };
        const { updatedStops, requests, transferredStop } = await dispatch(
          processFunction(transferRouteId, currentRoute, additionalData)
        );
        return {
          updatedStops,
          requests: [...data.requests, ...requests],
          transferredStop,
        };
      },
      Promise.resolve({ updatedStops: [], requests: [], transferredStop: [] })
    );
    return await dispatch(
      updateTransferEntities({
        ...dataToUpdate,
        transferRouteId,
      })
    );
  };

export const validateStopParcels =
  (toValidateParcelList) => async (dispatch) => {
    try {
      return await dispatch(
        validateStopMerge({
          parcels: toValidateParcelList,
          checkOpenRequests: true,
          splitStop: true,
        })
      );
    } catch (error) {
      dispatch(
        showNotification({
          type: NotificationType.ACTION,
          message: getErrorMessage(error),
          buttonText: DISMISS,
        })
      );
      return null;
    }
  };

export const getAffectedTransferStops =
  (selectedStops, conditions) => async (dispatch, getState) => {
    const state = getState();
    const stops = getDeliveryRouteStops(state);
    const affectedStops = getAffectedStops(selectedStops, stops, conditions);
    const validatedStops = await affectedStops.reduce(
      async (affected, stop) => {
        try {
          const { parcel = [] } = stop;
          const stopFromAPI = await dispatch(
            validateStopParcels(
              parcel.map((el) => ({
                parcelCode: el.parcelCode,
                routeDate: el.routeDate,
              }))
            )
          );
          const acc = await affected;
          if (!stopFromAPI) {
            throw new Error('Failed to validate stop');
          }
          return [...acc, { ...stop, ...stopFromAPI }];
        } catch {
          return [];
        }
      },
      Promise.resolve([])
    );
    if (validatedStops.length !== affectedStops.length) {
      throw new Error('Failed to validate stop');
    }
    return validatedStops;
  };
